import { useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Download, MapPin, Navigation } from "lucide-react";
import { BACKEND, mediaUrl } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { fmtNum } from "../lib/format";
import { Lightbox } from "./Gallery";

export const mapsUrl = (p) => p.google_maps_url || (p.lat && p.lng ? `https://www.google.com/maps/search/?api=1&query=${p.lat},${p.lng}` : null);
export const brochureHref = (p, lang) => (p.brochure_url ? mediaUrl(p.brochure_url) : `${BACKEND}/api/brochure/project/${p.slug}.pdf?lang=${lang}`);

export function ProjectLocation({ p }) {
  const { tr, lang } = useI18n();
  const b = (en, ar) => (lang === "ar" ? ar : en);
  const hl = p[`location_highlights_${lang}`]?.length ? p[`location_highlights_${lang}`] : p.location_highlights_en || [];
  const maps = mapsUrl(p);
  if (!hl.length && !maps) return null;
  const pageUrl = typeof window !== "undefined" ? `${window.location.origin}/developments/${p.slug}` : "";
  return (
    <section data-testid="project-location-section" className="glass rounded-sm p-6 md:p-10">
      <p className="eyebrow mb-3">{b("Location", "الموقع")}</p>
      <h2 className="h-display mb-2 text-3xl">{b("Location Highlights", "مميزات الموقع")}</h2>
      {tr(p, "location_caption") && <p data-testid="project-location-caption" className="mb-6 text-sm text-fg/55">{tr(p, "location_caption")}</p>}
      <div className="grid gap-10 md:grid-cols-[1fr_auto]">
        <ul data-testid="project-location-highlights" className="grid gap-3 sm:grid-cols-2">
          {hl.map((h) => (<li key={h} className="flex items-start gap-3 text-sm text-fg/80"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-cta" />{h}</li>))}
        </ul>
        <div className="flex flex-col items-center gap-3 text-center" data-testid="project-qr">
          <div className="rounded-sm bg-fg p-3"><QRCodeSVG value={pageUrl} size={128} bgColor="#F5F1E8" fgColor="#0A0A0A" title={b("Scan the code", "امسح الرمز")} /></div>
          <p className="text-xs uppercase tracking-[0.2em] text-cta">{b("Scan the code", "امسح الرمز")}</p>
        </div>
      </div>
      <div className="mt-8 flex flex-wrap gap-3">
        {maps && <a href={maps} target="_blank" rel="noopener noreferrer" data-testid="project-google-maps-button" className="btn-cta"><Navigation className="h-4 w-4" />{b("Open the location in Google Maps", "افتح الموقع في خرائط جوجل")}</a>}
        <a href={brochureHref(p, lang)} target="_blank" rel="noopener noreferrer" data-testid="project-location-brochure-button" className="btn-ghost"><Download className="h-4 w-4" />{b("Download the brochure (PDF)", "تحميل البروشور (PDF)")}</a>
      </div>
      {p.location_approximate && <p data-testid="project-location-approximate" className="mt-4 text-xs text-fg/45">{b("Approximate location — exact site details available on request.", "الموقع تقريبي — تفاصيل الموقع الدقيقة متاحة عند الطلب.")}</p>}
    </section>
  );
}

export function FloorPlanGroups({ p }) {
  const { tr, lang } = useI18n();
  const [open, setOpen] = useState({ g: 0, i: null });
  const groups = (p.floor_plan_groups || []).filter((g) => g.images?.length);
  if (!groups.length) return null;
  return (
    <section data-testid="project-floor-plans">
      <h2 className="h-display mb-8 text-3xl">{lang === "ar" ? "المخططات والتصاميم الداخلية" : "Floor Plans & Interiors"}</h2>
      {groups.map((g, gi) => (
        <div key={g.title_en} className="mb-10">
          <h3 className="eyebrow mb-4" data-testid={`floor-plan-group-${gi}`}>{tr(g, "title")}</h3>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            {g.images.map((src, i) => (
              <button key={src} type="button" data-testid={`floor-plan-${gi}-${i}`} onClick={() => setOpen({ g: gi, i })} className="overflow-hidden rounded-sm border border-fg/10 bg-fg">
                <img src={mediaUrl(src)} alt={`${tr(g, "title")} ${i + 1}`} loading="lazy" className="aspect-[1.41] w-full object-contain transition-transform duration-500 hover:scale-105" />
              </button>
            ))}
          </div>
        </div>
      ))}
      <Lightbox images={groups[open.g]?.images || []} index={open.i} onClose={() => setOpen({ g: 0, i: null })} title={tr(p, "title")} />
    </section>
  );
}

export function UnitsTable({ units, onInquire }) {
  const { tr, lang } = useI18n();
  const b = (en, ar) => (lang === "ar" ? ar : en);
  const rows = units.filter((u) => u.tower);
  if (!rows.length) return null;
  return (
    <div className="overflow-x-auto rounded-sm border border-fg/10" data-testid="project-units-table">
      <table className="w-full min-w-[640px] text-sm">
        <thead className="bg-surface text-start text-xs uppercase tracking-wider text-cta">
          <tr>{[b("Unit", "الوحدة"), b("Tower", "البرج"), b("Size", "المساحة"), b("Bedrooms", "غرف النوم"), b("Bathrooms", "دورات المياه"), b("Terrace", "السطح"), b("Price", "السعر"), ""].map((h) => (<th key={h} className="p-3 text-start font-semibold">{h}</th>))}</tr>
        </thead>
        <tbody>
          {rows.map((u) => (
            <tr key={u.id} className="border-t border-fg/10" data-testid={`unit-row-${u.slug}`}>
              <td className="p-3"><Link to={`/properties/${u.slug}`} className="text-fg hover:text-cta">{u.unit_group === "annex" ? b("Annex", "ملحق") : b("Typical", "متكرر")} · {u.unit_no}</Link></td>
              <td className="p-3">{u.tower}</td>
              <td className="p-3" dir="ltr">{fmtNum(u.size_sqm, lang)} {b("m²", "م²")}</td>
              <td className="p-3">{fmtNum(u.bedrooms, lang)}</td>
              <td className="p-3">{fmtNum(u.bathrooms, lang)}</td>
              <td className="p-3">{u.terrace_sqm ? `${fmtNum(u.terrace_sqm, lang)} ${b("m²", "م²")}` : "—"}</td>
              <td className="p-3 text-cta">{u.price_on_request ? b("Price on request", "السعر عند الطلب") : u.price}</td>
              <td className="p-3"><button type="button" data-testid={`unit-inquire-${u.slug}`} onClick={() => onInquire(u)} className="chip">{b("Inquire", "استفسر")}</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="sr-only">{tr(rows[0], "title")}</p>
    </div>
  );
}
